import { ChartsService } from './charts.service';
import { Injectable } from '@angular/core';
import * as R from 'ramda';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import {
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { InsightFiltersService } from './insight-global-filters.service';

import { APIService, KPI } from '../../@core/api';
import { YearsService } from '../../@core/utils/years.service';
import { PerimetersService } from './perimeters.service';

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export interface CapexObservableMap<
  T extends DeepPartial<KPI.BaseComputedNotation | KPI.BaseComputedNotation[]>,
> {
  [key: string]: Observable<T>;
}

export interface CapexLoadingMap {
  [key: string]: ReplaySubject<boolean>;
}

export interface BasicObservableMap<T> {
  [key: string]: Observable<T>;
}

/** Provide CAPEX for capex */
@Injectable({
  providedIn: 'root',
})
export class CapexService {
  // private globalCapex: CapexObservableMap<DeepPartial<KPI.GlobalComputedNotation[]>> = {};
  // private globalCapexLoading: CapexLoadingMap = {};

  private superCategoriesCapex: CapexObservableMap<
    DeepPartial<KPI.SuperCategoryComputedNotation[]>
  > = {};
  private superCategoriesCapexLoading: CapexLoadingMap = {};

  private categoriesCapex: CapexObservableMap<
    DeepPartial<KPI.SuperCategoryComputedNotation[]>
  > = {};
  private categoriesCapexLoading: CapexLoadingMap = {};

  private subCategoriesCapex: CapexObservableMap<
    DeepPartial<KPI.SuperCategoryComputedNotation[]>
  > = {};
  private subCategoriesCapexLoading: CapexLoadingMap = {};

  private assetTypesCapex: CapexObservableMap<
    DeepPartial<KPI.AssetTypeComputedNotation>[]
  > = {};
  private assetTypesCapexLoading: CapexLoadingMap = {};

  private assetsCapex: CapexObservableMap<
    DeepPartial<KPI.AssetTypeComputedNotation>[]
  > = {};
  private assetsCapexLoading: CapexLoadingMap = {};

  // private assetsCapex: CapexObservableMap<DeepPartial<KPI.AssetComputedNotation[]>> = {};
  // private assetsCapexLoading: CapexLoadingMap = {};

  private sitesCapex: CapexObservableMap<KPI.PerimeterComputedNotation[]> = {};
  private sitesCapexLoading: CapexLoadingMap = {};

  private perimetersCapex: CapexObservableMap<KPI.PerimeterComputedNotation[]> =
    {};
  private perimetersCapexLoading: CapexLoadingMap = {};

  private capexRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
    private years: YearsService,
    private insightFiltersService: InsightFiltersService,
    private chartsService: ChartsService,
  ) { }

  public static getEvolution(
    capex: DeepPartial<KPI.BaseComputedNotation>,
    startYear: number,
    endYear: number,
  ): number {
    const startNote = capex.capex[startYear].capex;
    const endNote = capex.capex[endYear].capex;

    return ((endNote - startNote) / startNote) * 100;
  }

  public refreshCapex(): void {
    this.capexRefresh$.next();
  }

  public getSuperCategoriesCapex(
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.SuperCategoryComputedNotation>[]>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.superCategoriesCapex[key]) {
      this.superCategoriesCapexLoading[key] = new ReplaySubject(1);

      // @ts-ignore
      this.superCategoriesCapex[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.capexRefresh$,
      ).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.superCategoriesCapexLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api
            .audit_capex_super_categories({
              start_year: startYear,
              end_year: endYear,
              buildings:
                PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            })
            .pipe(
              map((res) => res['results']),
              finalize(() => this.superCategoriesCapexLoading[key].next(false)),
            ),
        ),
        shareReplay(1),
      );
    }

    return [
      this.superCategoriesCapex[key],
      this.superCategoriesCapexLoading[key],
    ];
  }

  public getSuperCategoryCapex(
    superCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.SuperCategoryComputedNotation>>,
      Observable<boolean>,
    ] {
    const [capex$, loading$] = this.getSuperCategoriesCapex(
      scenario,
      forcedPerimeters,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((c) => c.super_category === superCategory)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  public getCategoriesCapex(
    superCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.CategoryComputedNotation>[]>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.categoriesCapex[key]) {
      this.categoriesCapexLoading[key] = new ReplaySubject(1);

      // @ts-ignore
      this.categoriesCapex[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.capexRefresh$,
      ).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.categoriesCapexLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api
            .audit_capex_categories(superCategory, {
              start_year: startYear,
              end_year: endYear,
              buildings:
                PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            })
            .pipe(
              map((res) => res['results']),
              finalize(() => this.categoriesCapexLoading[key].next(false)),
            ),
        ),
        shareReplay(1),
      );
    }

    return [this.categoriesCapex[key], this.categoriesCapexLoading[key]];
  }

  public getCategoryCapex(
    category: number,
    superCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.CategoryComputedNotation>>,
      Observable<boolean>,
    ] {
    const [capex$, loading$] = this.getCategoriesCapex(
      superCategory,
      scenario,
      forcedPerimeters,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((c) => c.category === category)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  public getSubCategoriesCapex(
    superCategory: number,
    category: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.SubCategoryComputedNotation>[]>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.subCategoriesCapex[key]) {
      this.subCategoriesCapexLoading[key] = new ReplaySubject(1);

      // @ts-ignore
      this.subCategoriesCapex[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.capexRefresh$,
      ).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.subCategoriesCapexLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api
            .audit_capex_subcategories(superCategory, category, {
              start_year: startYear,
              end_year: endYear,
              buildings:
                PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            })
            .pipe(
              map((res) => res['results']),
              finalize(() => this.subCategoriesCapexLoading[key].next(false)),
            ),
        ),
        shareReplay(1),
      );
    }

    return [this.subCategoriesCapex[key], this.subCategoriesCapexLoading[key]];
  }

  public getSubCategoryCapex(
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.SubCategoryComputedNotation>>,
      Observable<boolean>,
    ] {
    const [capex$, loading$] = this.getSubCategoriesCapex(
      superCategory,
      category,
      scenario,
      forcedPerimeters,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((c) => c.sub_category === subCategory)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  public getAssetTypesCapex(
    superCategory: number,
    category: number,
    subCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.AssetTypeComputedNotation>[]>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.assetTypesCapex[key]) {
      this.assetTypesCapexLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.AssetTypeComputedNotation[] = [];

      this.assetTypesCapex[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.capexRefresh$,
      ).pipe(
        tap(() => (accumulator = [])),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.assetTypesCapexLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) => {
          const res = this.api.audit_capex_asset_types(
            superCategory,
            category,
            subCategory,
            {
              start_year: startYear,
              end_year: endYear,
              buildings:
                PerimetersService.getBuildingsIds(perimeters).join(','),
              scenario,
              include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
            },
          );

          return res.pipe(
            finalize(() => this.assetTypesCapexLoading[key].next(false)),
          );
        }),
        map((capex) => {
          accumulator = [].concat(accumulator, capex);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.assetTypesCapex[key], this.assetTypesCapexLoading[key]];
  }

  public getAssetTypeCapex(
    assetType: number,
    superCategory: number,
    category: number,
    subCategory: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.AssetTypeComputedNotation>>,
      Observable<boolean>,
    ] {
    const [capex$, loading$] = this.getAssetTypesCapex(
      superCategory,
      category,
      subCategory,
      scenario,
      forcedPerimeters,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((price) => price.asset_type === assetType)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  public getAssetsCapex(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    scenario,
    forcedPerimeters?: KPI.Perimeter[],
  ): [
      Observable<DeepPartial<KPI.AssetComputedNotation>[]>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.assetsCapex[key]) {
      this.assetsCapexLoading[key] = new ReplaySubject(1);

      this.assetsCapex[key] = combineLatest(
        this.getPerimetersObservable(forcedPerimeters),
        this.years.latestYears$,
        this.insightFiltersService.selectedGlobalFilter$,
        this.capexRefresh$,
      ).pipe(
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.assetsCapexLoading[key].next(true)),
        switchMap(([perimeters, [startYear, endYear], selectedGlobalFilter]) =>
          this.api
            .audit_capex_assets(
              superCategory,
              category,
              subCategory,
              assetType,
              {
                start_year: startYear,
                end_year: endYear,
                buildings:
                  PerimetersService.getBuildingsIds(perimeters).join(','),
                scenario,
                include_hypothesis: selectedGlobalFilter === KPI.InsightGlobalFilter.ALL_INVESTMENTS ? 1 : 0,
              },
            )
            .pipe(finalize(() => this.assetsCapexLoading[key].next(false))),
        ),
        shareReplay(1),
      );
    }

    return [this.assetsCapex[key], this.assetsCapexLoading[key]];
  }

  /** @todo Optimzation - Reach a new endpoint to filter data on server side ? */
  public getAssetCapex(
    superCategory: number,
    category: number,
    subCategory: number,
    assetType: number,
    asset: number,
    scenario: number,
    forcedPerimeters?: KPI.Perimeter[],
  ): [Observable<DeepPartial<KPI.AssetComputedNotation>>, Observable<boolean>] {
    const [capex$, loading$] = this.getAssetsCapex(
      superCategory,
      category,
      subCategory,
      assetType,
      scenario,
      forcedPerimeters,
    );

    return [
      capex$.pipe(
        filter((capex) => capex !== undefined),
        map((capex) => capex.find((c) => c.asset === asset)),
      ),
      loading$,
    ];
  }

  public getSitesCapex(
    superCategory: number,
    category: number,
    subCategory: number,
    scenario: number,
  ): [
      Observable<DeepPartial<KPI.PerimeterComputedNotation[]>>,
      Observable<boolean>,
    ] {
    const key = this.getKey(arguments);

    if (!this.sitesCapex[key]) {
      this.sitesCapexLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.PerimeterComputedNotation[] = [];

      this.sitesCapex[key] = combineLatest(
        this.years.latestYears$,
        this.capexRefresh$,
      ).pipe(
        tap(() => (accumulator = [])),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.sitesCapexLoading[key].next(true)),
        switchMap(([[startYear, endYear]]) =>
          this.api
            .audit_capex_sites(superCategory, category, subCategory, {
              start_year: startYear,
              end_year: endYear,
              scenario,
            })
            .pipe(finalize(() => this.sitesCapexLoading[key].next(false))),
        ),
        map((capex) => {
          accumulator = [].concat(accumulator, capex);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.sitesCapex[key], this.sitesCapexLoading[key]];
  }

  public getSiteCapex(
    site: number,
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
  ): [
      Observable<DeepPartial<KPI.PerimeterComputedNotation>>,
      Observable<boolean>,
    ] {
    const [capex$, loading$] = this.getSitesCapex(
      superCategory,
      category,
      subCategory,
      scenario,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((c) => c.perimeter === site)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  public getPerimetersCapex(
    superCategory: number,
    category: number,
    subCategory: number,
    site: number,
    scenario: number,
  ): [Observable<KPI.PerimeterComputedNotation[]>, Observable<boolean>] {
    const key = this.getKey(arguments);

    if (!this.perimetersCapex[key]) {
      this.perimetersCapexLoading[key] = new ReplaySubject(1);
      let accumulator: KPI.PerimeterComputedNotation[] = [];

      this.perimetersCapex[key] = combineLatest(
        this.years.latestYears$,
        this.capexRefresh$,
      ).pipe(
        tap(() => (accumulator = [])),
        filter(([perimeters]) => perimeters.length > 0),
        tap(() => this.perimetersCapexLoading[key].next(true)),
        switchMap(([[startYear, endYear]]) =>
          this.api
            .audit_capex_perimeters(
              superCategory,
              category,
              subCategory,
              site,
              {
                start_year: startYear,
                end_year: endYear,
                scenario,
              },
            )
            .pipe(finalize(() => this.perimetersCapexLoading[key].next(false))),
        ),
        map((capex) => {
          accumulator = [].concat(accumulator, capex);
          return accumulator;
        }),
        shareReplay(1),
      );
    }

    return [this.perimetersCapex[key], this.perimetersCapexLoading[key]];
  }

  public getPerimeterCapex(
    perimeter: number,
    site: number,
    subCategory: number,
    category: number,
    superCategory: number,
    scenario: number,
  ): [Observable<KPI.PerimeterComputedNotation>, Observable<boolean>] {
    const [capex$, loading$] = this.getPerimetersCapex(
      superCategory,
      category,
      subCategory,
      site,
      scenario,
    );

    return [
      capex$.pipe(
        map((capex) => capex.find((c) => c.perimeter === perimeter)),
        filter((capex) => capex !== undefined),
      ),
      loading$,
    ];
  }

  private getKey(args: IArguments): string {
    return JSON.stringify(R.pickBy(R.complement(R.isNil), args));
  }

  private getPerimetersObservable(
    perimeters?: KPI.Perimeter[],
  ): Observable<KPI.Perimeter[]> {
    if (perimeters) {
      return of(perimeters).pipe(shareReplay(1));
    } else {
      return this.perimetersService.perimeters$;
    }
  }

  private capexFiltered: BasicObservableMap<KPI.SimpleCapex> = {};
  public getCapexFiltered(options): Observable<KPI.SimpleCapex> {
    const key = this.getKey(arguments);
    if (!this.capexFiltered[key]) {
      this.capexFiltered[key] = combineLatest([
        this.chartsService._energyHasChanged$,
        this.chartsService._capexHasChanged$,
      ]).pipe(
        switchMap(() => this.api.get_capex_filtered(options)),
        shareReplay(1),
      );
    }
    return this.capexFiltered[key];
  }
}
